/**
 * Tratamento de dados.
 *
 * Documento estático, registrado no App Registration do Entra como "Privacy
 * statement URL" e exibido na tela de consentimento da Microsoft. Mudar a URL
 * exige pedido formal ao IAM (DEP-27).
 *
 * Cada afirmação aqui é verificável no código. Se o comportamento mudar, este
 * texto muda junto — descrição de privacidade que não corresponde ao sistema é
 * pior que nenhuma.
 *
 * Revisão jurídica e de privacidade pendente.
 */
import { usePageMeta } from '../hooks/usePageMeta.js';
import { Header } from '../components/Header.jsx';
import { Panel } from '../components/Panel.jsx';

export function Privacy() {
  usePageMeta('AI Hub — tratamento de dados');

  return (
    <>
      <Header badge="tratamento de dados" />

      <main>
        <h1>Tratamento de dados</h1>
        <p className="lead">
          Esta página descreve, de forma verificável, quais dados o AI Hub lê da sua conta
          corporativa, o que guarda e por quanto tempo. É uma descrição técnica do que o sistema faz
          — não substitui a política de privacidade da Iguatemi.
        </p>

        <Panel
          title="O que lemos da sua conta Microsoft"
          footer={
            <>
              Nenhum outro dado do diretório é solicitado. A permissão pedida é{' '}
              <code>User.Read</code>, que alcança o seu próprio perfil e mais nada.
            </>
          }
        >
          <ul className="checks">
            <li>
              <b>
                Identificador do objeto (<code>oid</code>)
              </b>{' '}
              — o código que identifica você no diretório. É por ele que a plataforma sabe de quem é
              cada acesso.
            </li>
            <li>
              <b>Nome de usuário principal e nome de exibição</b> — para mostrar quem está logado e
              para identificar o solicitante no chamado do ServiceNow.
            </li>
            <li>
              <b>Grupos de segurança atribuídos a esta aplicação</b> — apenas os que começam com{' '}
              <code>SEC-AIHUB-</code>. A plataforma <b>não</b> recebe a lista completa dos grupos a
              que você pertence.
            </li>
          </ul>
        </Panel>

        <Panel
          title="O que fica registrado"
          footer="A sessão expira após 8 horas sem uso, e em 12 horas de qualquer forma. Ao sair, ela é apagada do servidor imediatamente."
        >
          <ul className="checks">
            <li>
              <b>Sessão</b> — enquanto você está logado, seus dados de sessão ficam no servidor, não
              no navegador. O navegador recebe apenas um identificador aleatório, sem significado
              próprio.
            </li>
            <li>
              <b>Registro de acesso</b> — cada vez que você abre um item do portfólio ou solicita
              acesso a um, fica gravado quem, o quê e quando. É o que permite responder a uma
              auditoria.
            </li>
            <li>
              <b>Solicitações de acesso</b> — o item pedido e o número do chamado aberto no
              ServiceNow.
            </li>
          </ul>
        </Panel>

        <Panel
          title="Conversas com os agentes de IA"
          footer="Vale apenas para os agentes construídos pela companhia no Microsoft Copilot Studio. Ferramentas de terceiros — Claude, Canva, HeyGen e as demais do portfólio — não enviam nada para cá: o que você faz nelas fica nelas."
        >
          <p className="lead">
            Quando você conversa com um agente de IA da Iguatemi, a plataforma recebe e guarda{' '}
            <b>o que foi perguntado e o que o agente respondeu</b>, junto de quem falou, com qual
            agente, quando e por quanto tempo.
          </p>

          <ul className="checks">
            <li>
              <b>Por que</b> — para medir uso, identificar onde os agentes erram e melhorá-los, e
              para responder a auditoria sobre o uso de IA na companhia.
            </li>
            <li>
              <b>Quem pode ler</b> — apenas administradores da plataforma. Cada leitura de conversa
              fica registrada: quem abriu, de quem era e quando.
            </li>
            <li>
              <b>O que não é guardado</b> — anexos enviados ao agente. Só o número deles.
            </li>
          </ul>

          <p className="lead">
            Trate a conversa com um agente como trata um e-mail corporativo: ela é registrada, e
            pode ser lida por quem administra a plataforma.
          </p>
        </Panel>

        <Panel title="O que a plataforma não faz">
          <ul className="checks">
            <li>
              Não armazena sua senha. A autenticação acontece inteiramente na Microsoft — o AI Hub
              nunca vê sua credencial.
            </li>
            <li>Não envia dados para fora do ambiente da Iguatemi.</li>
            <li>
              Não concede acesso por conta própria. Toda concessão passa por aprovação no ServiceNow
              e é efetivada por grupo do diretório.
            </li>
            <li>
              Não lê conteúdo dos sistemas do portfólio. Uma exceção, descrita acima: os agentes de
              IA construídos pela companhia enviam suas conversas para cá.
            </li>
          </ul>
        </Panel>

        <Panel
          title="Dúvidas e correções"
          footer="Documento técnico, mantido junto do código da plataforma. Revisão jurídica e de privacidade pendente."
        >
          <p className="lead">
            Para pedir a correção ou a remoção de um registro, ou para entender por que um acesso
            foi negado, procure a equipe de <b>Inovação e IA</b> pelo canal interno de atendimento.
          </p>
        </Panel>
      </main>
    </>
  );
}
